/**
 * FeatureFlagProvider -- React Context Provider
 *
 * Wraps the app to provide feature flag context.
 * Triggers prefetch on mount (post-login) and re-renders consumers on cache updates.
 */

import React, { createContext, useContext, useEffect, useCallback, useState, useMemo } from 'react'
import { featureFlagService } from './featureFlagService'
import { IFeatureFlagState } from './featureFlags.types'
import { FEATURE_FLAG_REGISTRY } from './featureFlagRegistry'
import { NO_CURRENT_STORE } from '@src/constants'
import { logger } from './featureFlagLogger'

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface IFeatureFlagContextValue {
    /** Get a flag's current state */
    getFlag: (flagName: string, storeId?: string | null) => IFeatureFlagState
    /** Current store ID for implicit store-scoped lookups */
    currentStoreId: string | null
    /** Trigger count -- incremented on every cache update to force re-renders */
    version: number
}

const FeatureFlagContext = createContext<IFeatureFlagContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface IFeatureFlagProviderProps {
    children: React.ReactNode
    /** Current active store ID -- updated on store switch */
    storeId?: string | null
}

export function FeatureFlagProvider ({ children, storeId = null }: IFeatureFlagProviderProps): React.ReactElement {
    const [version, setVersion] = useState(0)

    // Subscribe to cache updates
    useEffect(() => {
        const unsubscribe = featureFlagService.subscribe(() => {
            setVersion((v) => v + 1)
        })
        return unsubscribe
    }, [])

    // Prefetch global flags on mount (runs once after login)
    useEffect(() => {
        logger.info('Provider mounted -- prefetching global flags')
        featureFlagService.prefetchGlobal()
    }, [])

    // Fetch and then log store-scoped flags after the cache reaches a settled state.
    useEffect(() => {
        if (!storeId || storeId === NO_CURRENT_STORE) return

        let cancelled = false
        logger.info('Store changed -- prefetching store flags', { storeId })
        featureFlagService.prefetchForStore(storeId).then(() => {
            if (cancelled) return

            const storeScopedFlags = Object.keys(FEATURE_FLAG_REGISTRY).filter(
                (name) => FEATURE_FLAG_REGISTRY[name].isStoreScoped,
            )
            logger.debug('--- Per-store feature flag status ---', { storeId, flagCount: storeScopedFlags.length })
            storeScopedFlags.forEach((flagName) => {
                const state = featureFlagService.get(flagName, storeId)
                logger.debug(
                    state.enabled
                        ? 'FLAG ENABLED for this store'
                        : 'FLAG DISABLED for this store',
                    {
                        flagName,
                        storeId,
                        enabled: state.enabled,
                        source: state.source,
                        error: state.error,
                        loading: state.loading,
                    },
                )
            })
            logger.debug('--- End per-store flag status ---')
        })

        return () => { cancelled = true }
    }, [storeId])

    // NOTE: No TypeScript annotations on arrow params -- Babel 7.15 cannot parse them (see lesson)
    const getFlag = useCallback((flagName, explicitStoreId) => {
        return featureFlagService.get(flagName, explicitStoreId ?? storeId)
    }, [storeId])

    const value: IFeatureFlagContextValue = useMemo(() => ({ getFlag, currentStoreId: storeId, version }), [getFlag, storeId, version])

    return (
        <FeatureFlagContext.Provider value={value}>
            {children}
        </FeatureFlagContext.Provider>
    )
}

// ---------------------------------------------------------------------------
// Hook helper (used by useFeatureFlag / useFeatureFlags)
// ---------------------------------------------------------------------------

export function useFeatureFlagContext (): IFeatureFlagContextValue {
    const ctx = useContext(FeatureFlagContext)
    if (!ctx) {
        throw new Error(
            '[FeatureFlags] useFeatureFlag must be used within <FeatureFlagProvider>. ' +
            'Ensure FeatureFlagProvider wraps your component tree after login.',
        )
    }
    return ctx
}
